import React, { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError, AccountInfo } from '@azure/msal-browser';
import { Client } from "@microsoft/microsoft-graph-client";
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Divider,
  Paper,
  Tooltip,
  Chip,
  Snackbar,
  ListItemButton,
  FormHelperText
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  ContentCopy as ContentCopyIcon,
  Link as LinkIcon,
  Assignment as AssignmentIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Info as InfoIcon,
  Code as CodeIcon,
  Warning as WarningIcon,
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon
} from "@mui/icons-material";
import DoneIcon from "@mui/icons-material/Done";
import { Application } from "@microsoft/microsoft-graph-types";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import TokenService from '../utils/tokenService';

// Custom types for our policy management
interface ClaimsMappingPolicy {
  id: string;
  displayName: string;
  definition: string[];
  description?: string;
  isOrganizationDefault?: boolean;
}

interface ServicePrincipal {
  tags?: string[];
  id: string;
  appId: string;
  displayName: string;
  appOwnerOrganizationId?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface ClaimMappingDefinition {
  source: string;
  id: string;
  saml?: boolean;
  jwtClaimType?: string;
  defaultValue?: string;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// Example claim mapping templates
const claimTemplates: { [key: string]: ClaimMappingDefinition[] } = {
  "User Department": [
    {
      source: "user",
      id: "department",
      jwtClaimType: "department"
    }
  ],
  "User Extension Attribute": [
    {
      source: "user",
      id: "extension_<guid>_AttributeName",
      jwtClaimType: "extension_AttributeName"
    }
  ],
  "Custom Group Claim": [
    {
      source: "user",
      id: "groups",
      jwtClaimType: "groups"
    }
  ],
  "SAML Name Identifier": [
    {
      source: "user",
      id: "userprincipalname",
      saml: true,
      jwtClaimType: "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
    }
  ],
  "Employee ID": [
    {
      source: "user",
      id: "employeeid",
      jwtClaimType: "employeeid"
    }
  ],
  "Job Title": [
    {
      source: "user",
      id: "jobTitle",
      jwtClaimType: "jobTitle"
    }
  ]
};

// Explanation for the types of claims and examples
const claimsExplanations = [
  {
    title: "Standard User Claims",
    description: "Built-in user attributes from Azure AD",
    examples: ["department", "jobTitle", "mail", "employeeId"]
  },
  {
    title: "Directory Extension Claims",
    description: "Custom attributes added to Azure AD objects",
    examples: ["extension_<guid>_CustomAttribute", "extension_<guid>_LegacyID"]
  },
  {
    title: "Conditional Claims",
    description: "Claims that are included based on specific conditions",
    examples: ["Include claim when user's application is in a specified application group", "Include claim when user's security group is in a specified list"]
  }
];

// Sample JSON structures to help users understand the format
const policyExamples = {
  basic: 
`{
  "ClaimsMappingPolicy": {
    "Version": 1,
    "IncludeBasicClaimSet": "true",
    "ClaimsSchema": [
      {
        "Source": "user",
        "ID": "department",
        "JwtClaimType": "department"
      },
      {
        "Source": "user",
        "ID": "extension_<guid>_CustomAttribute",
        "JwtClaimType": "customAttribute"
      }
    ]
  }
}`,
  advanced: 
`{
  "ClaimsMappingPolicy": {
    "Version": 1,
    "IncludeBasicClaimSet": "true",
    "ClaimsSchema": [
      {
        "Source": "user",
        "ID": "department", 
        "JwtClaimType": "department"
      }
    ],
    "IncludeBasicClaimSet": "true",
    "OptionalClaims": {
      "idtoken": [{
          "name": "upn",
          "source": null,
          "essential": false,
          "additionalProperties": ["include_externally_authenticated_upn"]
      }]
    }
  }
}`
};

const parseClaimsDefinition = (definitionArray: string[]): any => {
  try {
    const definitionString = definitionArray.join('');
    return JSON.parse(definitionString);
  } catch (error) {
    console.error("Failed to parse claims definition:", error);
    return null;
  }
};

// Add this function near the other utility functions in ClaimsMappingPolicyTool.tsx
// (e.g., near parseClaimsDefinition)

const validateClaimsMappingPolicyJson = (jsonString: string): { isValid: boolean; error?: string; parsedJson?: any } => {
  try {
    // Check if JSON string is empty
    if (!jsonString.trim()) {
      return { 
        isValid: false, 
        error: "Policy JSON cannot be empty" 
      };
    }

    // Try to parse the JSON
    const parsedJson = JSON.parse(jsonString);

    // Verify it's a valid Claims Mapping Policy structure
    if (!parsedJson.ClaimsMappingPolicy) {
      return {
        isValid: false,
        error: "Invalid policy format: Missing ClaimsMappingPolicy object",
        parsedJson
      };
    }

    // Check for required properties
    const { ClaimsMappingPolicy } = parsedJson;
    
    // Version is required
    if (!('Version' in ClaimsMappingPolicy)) {
      return {
        isValid: false,
        error: "Invalid policy format: Missing Version property",
        parsedJson
      };
    }

    // Check if IncludeBasicClaimSet is specified
    if (!('IncludeBasicClaimSet' in ClaimsMappingPolicy)) {
      return {
        isValid: false,
        error: "Missing IncludeBasicClaimSet property",
        parsedJson
      };
    }

    // Check ClaimsSchema if present
    if (ClaimsMappingPolicy.ClaimsSchema && Array.isArray(ClaimsMappingPolicy.ClaimsSchema)) {
      // Validate each claim in the schema
      for (let i = 0; i < ClaimsMappingPolicy.ClaimsSchema.length; i++) {
        const claim = ClaimsMappingPolicy.ClaimsSchema[i];
        
        if (!claim.Source) {
          return {
            isValid: false,
            error: `Claim at index ${i} is missing required Source property`,
            parsedJson
          };
        }
        
        if (!claim.ID) {
          return {
            isValid: false,
            error: `Claim at index ${i} is missing required ID property`,
            parsedJson
          };
        }
        
        // Check if either JwtClaimType or SamlClaimType is present
        if (!claim.JwtClaimType && !claim.SamlClaimType) {
          return {
            isValid: false,
            error: `Claim at index ${i} is missing either JwtClaimType or SamlClaimType property`,
            parsedJson
          };
        }
      }
    }
    
    // If we made it here, the JSON is valid
    return {
      isValid: true,
      parsedJson
    };
  } catch (error: any) {
    // Handle JSON parsing errors
    return {
      isValid: false,
      error: `JSON parsing error: ${error.message}`
    };
  }
};

interface ClaimsMappingPolicyToolProps {
  onBack: () => void;
  accessToken: string;
}

const ClaimsMappingPolicyTool: React.FC<ClaimsMappingPolicyToolProps> = ({ onBack, accessToken }) => {
  // MSAL + Token service for JIT scope elevation
  const { instance, accounts } = useMsal();
  const tokenService = React.useMemo(() => TokenService.getInstance(instance), [instance]);
  const [loading, setLoading] = useState<boolean>(true);
  const [policies, setPolicies] = useState<ClaimsMappingPolicy[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [servicePrincipals, setServicePrincipals] = useState<ServicePrincipal[]>([]);
  
  // UI state
  const [selectedPolicy, setSelectedPolicy] = useState<ClaimsMappingPolicy | null>(null);
  const [selectedPolicyData, setSelectedPolicyData] = useState<any>(null);
  const [selectedPolicyJson, setSelectedPolicyJson] = useState<string>("");
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [newPolicyName, setNewPolicyName] = useState<string>("");
  const [newPolicyDescription, setNewPolicyDescription] = useState<string>("");
  const [newPolicyJson, setNewPolicyJson] = useState<string>("");
  const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState<boolean>(false);
  const [selectedAppId, setSelectedAppId] = useState<string>("");
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [templateDialogOpen, setTemplateDialogOpen] = useState<boolean>(false);
  const [jsonError, setJsonError] = useState<string>("");
  const [policyAssignments, setPolicyAssignments] = useState<{[policyId: string]: string[]}>({});
  const [appSearchQuery, setAppSearchQuery] = useState<string>(""); // For the search input
  const [filteredServicePrincipals, setFilteredServicePrincipals] = useState<ServicePrincipal[]>([]); // For the filtered list
  const [showAllAppTypes, setShowAllAppTypes] = useState<boolean>(false);

  // Create Graph client using read-only token provided by parent (Policy.Read.All, etc.)
  const getGraphClient = useCallback(async () => {
    if (!accessToken) {
      throw new Error("Access token is missing. Close this tool and try again.");
    }
    return Client.init({
      authProvider: (done) => done(null, accessToken)
    });
  }, [accessToken]);

  // Acquire a token for specific scopes (JIT), falling back to a popup if interaction is required
  const getTokenForScopes = useCallback(async (scopes: string[]): Promise<string> => {
    if (!accounts || accounts.length === 0) {
      throw new Error('No signed-in account available.');
    }
    const account: AccountInfo = accounts[0];
    try {
      return await tokenService.getToken(scopes, account);
    } catch (err: any) {
      // Attempt interactive consent if required
      if (err instanceof InteractionRequiredAuthError) {
        const result = await instance.acquireTokenPopup({ scopes, account });
        return result.accessToken;
      }
      // Some environments throw a generic error code; try interactive as a best-effort fallback
      const result = await instance.acquireTokenPopup({ scopes, account });
      return result.accessToken;
    }
  }, [accounts, instance, tokenService]);

  // Create a Graph client with a token for the provided scopes (used for write operations)
  const getGraphClientWithScopes = useCallback(async (scopes: string[]) => {
    const scopedToken = await getTokenForScopes(scopes);
    return Client.init({
      authProvider: (done) => done(null, scopedToken)
    });
  }, [getTokenForScopes]);

  // Fetch policies from Microsoft Graph
  const fetchPolicies = useCallback(async () => {
    setLoading(true);
    try {
      const client = await getGraphClient();
      const response = await client
        .api('/policies/claimsMappingPolicies')
        .get();
      
      setPolicies(response.value);
      
      // Fetch assignments for each policy
      const assignmentsMap: {[policyId: string]: string[]} = {};
      for (const policy of response.value) {
        try {
          const assignments = await client
            .api(`/policies/claimsMappingPolicies/${policy.id}/appliesTo`)
            .get();
          
          assignmentsMap[policy.id] = assignments.value.map((sp: any) => sp.id);
        } catch (assignError) {
          console.error(`Error fetching assignments for policy ${policy.id}:`, assignError);
        }
      }
      setPolicyAssignments(assignmentsMap);
    } catch (error) {
      console.error("Error fetching claims mapping policies:", error);
      setErrorMessage("Failed to fetch claims mapping policies. Make sure you have the appropriate permissions.");
    } finally {
      setLoading(false);
    }
  }, [getGraphClient]);

  // Fetch service principals from Microsoft Graph
  // 1. Remove policyAssignments from fetchServicePrincipals dependencies
  const fetchServicePrincipals = useCallback(async (fetchAll: boolean = false, preserveAssignments: boolean = true) => {
    setLoading(true);
    try {
      const client = await getGraphClient();
      const allServicePrincipals: ServicePrincipal[] = [];
      
      // Use a more targeted approach with filtering
      let request = client
        .api('/servicePrincipals')
        .select('id,appId,displayName,appOwnerOrganizationId,tags,servicePrincipalType');
        
      // Only apply filtering if fetchAll is false
      if (!fetchAll) {
        request = request.filter("servicePrincipalType eq 'Application'");
      }
      
      let response = await request.top(100).get();

      allServicePrincipals.push(...response.value);

      // Handle pagination properly using the nextLink
      while (response["@odata.nextLink"]) {
        console.log("Fetching next page of service principals...");
        response = await client.api(response["@odata.nextLink"]).get();
        allServicePrincipals.push(...response.value);
      }

      // Only preserve assignments when explicitly requested
      // (this breaks the circular dependency)
      let assignedServicePrincipalIds = new Set<string>();
      if (preserveAssignments) {
        // Safely collect all assigned service principal IDs for preservation
        Object.values(policyAssignments).forEach(spIds => {
          spIds.forEach(id => assignedServicePrincipalIds.add(id));
        });
      }

      // Further refine the list to match ManifestEditor's behavior
      const filteredSPs = allServicePrincipals.filter(sp => {
        // Always include service principals that have policy assignments
        if (preserveAssignments && assignedServicePrincipalIds.has(sp.id)) {
          return true;
        }
        
        // Rest of your existing filtering logic
        if (sp.tags && Array.isArray(sp.tags)) {
          if (sp.tags.some(tag => 
            tag.includes("Web") || 
            tag.includes("SPA") || 
            tag.includes("SinglePageApplication")
          )) {
            return true;
          }
        }
        
        // Keep Microsoft first-party apps that support claims
        const isMicrosoftApp = sp.appOwnerOrganizationId === "f8cdef31-a31e-4b4a-93e4-5f571e91255a";
        if (isMicrosoftApp && sp.displayName) {
          const name = sp.displayName.toLowerCase();
          if (
            name.includes("azure") || 
            name.includes("microsoft") || 
            name.includes("office") ||
            name.includes("graph")
          ) {
            return true;
          }
        }
        
        // Include any app with "API" in tags
        if (sp.tags && sp.tags.some(tag => tag.includes("API"))) {
          return true;
        }
        
        return false;
      });

      // If fetchAll is true, use all SPs, otherwise use filtered + assigned SPs
      const resultSPs = fetchAll ? allServicePrincipals : filteredSPs;
      
      // Sort by display name
      resultSPs.sort((a, b) => 
        (a.displayName || '').localeCompare(b.displayName || '')
      );

      setServicePrincipals(resultSPs);
      setFilteredServicePrincipals(resultSPs);
    } catch (error: any) {
      console.error("Error fetching service principals:", error);
      setErrorMessage(`Failed to fetch service principals: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [getGraphClient]); // Remove policyAssignments from dependency array

  // 2. Update the toggle function to use the new parameter
  const toggleAppTypesFilter = () => {
    setShowAllAppTypes(!showAllAppTypes);
    fetchServicePrincipals(!showAllAppTypes, true); // Keep assignments when toggling
  };

  // 3. Create a separate function for fetching missing service principals
  const fetchMissingServicePrincipals = useCallback(async () => {
    if (Object.keys(policyAssignments).length === 0) return;
    
    try {
      const client = await getGraphClient();
      
      // Find service principal IDs from assignments that aren't in our current list
      const missingIds = new Set<string>();
      
      Object.values(policyAssignments).forEach(spIds => {
        spIds.forEach(id => {
          if (!servicePrincipals.some(sp => sp.id === id)) {
            missingIds.add(id);
          }
        });
      });
      
      if (missingIds.size === 0) return;
      
      console.log(`Fetching ${missingIds.size} missing service principals...`);
      
      // Fetch the missing service principals without causing a circular update
      const additionalServicePrincipals: ServicePrincipal[] = [];
      
      // Process in batches to avoid URL length limits
      const batchSize = 20;
      const missingIdArray = Array.from(missingIds);
      
      for (let i = 0; i < missingIdArray.length; i += batchSize) {
        const batch = missingIdArray.slice(i, i + batchSize);
        const filterString = batch.map(id => `id eq '${id}'`).join(' or ');
        
        try {
          const response = await client
            .api('/servicePrincipals')
            .filter(filterString)
            .select('id,appId,displayName,appOwnerOrganizationId,tags,servicePrincipalType')
            .get();
          
          if (response.value && response.value.length > 0) {
            additionalServicePrincipals.push(...response.value);
          }
        } catch (error) {
          console.error("Error fetching missing service principals:", error);
        }
      }
      
      if (additionalServicePrincipals.length > 0) {
        // Add missing service principals to our existing list in a single update
        setServicePrincipals(prevSPs => {
          const combined = [...prevSPs];
          additionalServicePrincipals.forEach(newSP => {
            if (!combined.some(sp => sp.id === newSP.id)) {
              combined.push(newSP);
            }
          });
          
          // Sort combined list
          return combined.sort((a, b) => 
            (a.displayName || '').localeCompare(b.displayName || '')
          );
        });
      }
    } catch (error) {
      console.error("Error in fetchMissingServicePrincipals:", error);
    }
  }, [getGraphClient, policyAssignments, servicePrincipals]);

  const getDefaultPolicyTemplate = () => {
    return JSON.stringify({
      ClaimsMappingPolicy: {
        Version: 1,
        IncludeBasicClaimSet: "true",
        ClaimsSchema: []
      }
    }, null, 2);
  }; 
  
  const resetNewPolicyForm = () => {
    setNewPolicyName("");
    setNewPolicyDescription("");
    setNewPolicyJson("");
    setNewPolicyJson(getDefaultPolicyTemplate()); 
    setJsonError("");
  };

  const handleOpenCreateDialog = () => {
    // Reset all form fields before opening the dialog
    resetNewPolicyForm();
    setCreateDialogOpen(true);
  };

  // 4. Update the initial data loading to use a well-defined sequence with a loading state
  useEffect(() => {
    if (accessToken) {
      const loadData = async () => {
        setLoading(true);
        try {
          await fetchPolicies();
          await fetchServicePrincipals(showAllAppTypes, false);
        } finally {
          setLoading(false);
        }
      };
      loadData();
    }
  }, [accessToken, fetchPolicies, fetchServicePrincipals, showAllAppTypes]);


  // 5. Add a separate effect to fetch missing service principals AFTER the initial load
  // This will run only when policyAssignments or servicePrincipals change
  useEffect(() => {
    // Only run if we have both policies and service principals loaded
    if (Object.keys(policyAssignments).length > 0 && servicePrincipals.length > 0) {
      fetchMissingServicePrincipals();
    }
  }, [policyAssignments, servicePrincipals, fetchMissingServicePrincipals]);

  useEffect(() => {
    if (!appSearchQuery.trim()) {
      // If search is empty, show all service principals
      setFilteredServicePrincipals(servicePrincipals);
    } else {
      // Otherwise, filter based on displayName or appId
      const lowercaseQuery = appSearchQuery.toLowerCase();
      const filtered = servicePrincipals.filter(sp =>
        (sp.displayName && sp.displayName.toLowerCase().includes(lowercaseQuery)) ||
        (sp.appId && sp.appId.toLowerCase().includes(lowercaseQuery))
      );
      setFilteredServicePrincipals(filtered);
    }
  }, [servicePrincipals, appSearchQuery]); // Re-run when SPs or query changes  

  // Handle policy selection
  const handleSelectPolicy = (policy: ClaimsMappingPolicy) => {
    setSelectedPolicy(policy);
    const parsedData = parseClaimsDefinition(policy.definition);
    setSelectedPolicyData(parsedData);
    setSelectedPolicyJson(JSON.stringify(parsedData, null, 2));
    setIsEditMode(false);
    setTabIndex(0);
  };

  // Update the handleCreatePolicy function to remove the description field
  const handleCreatePolicy = async () => {
    if (!newPolicyName.trim()) {
      setErrorMessage("Policy name is required");
      return;
    }

    // Validate JSON before attempting to create policy
    const validationResult = validateClaimsMappingPolicyJson(newPolicyJson);
    
    if (!validationResult.isValid) {
      setJsonError(validationResult.error || "Invalid JSON format");
      return;
    }
    
    try {
      setLoading(true);
      // JIT elevate to write scope
      const client = await getGraphClientWithScopes(["Policy.ReadWrite.ApplicationConfiguration"]);
      
      // Create policy object WITHOUT description field
      const newPolicy = {
        displayName: newPolicyName.trim(),
        definition: [JSON.stringify(validationResult.parsedJson)]
        // No description field - completely removed
      };
      
      console.log('Creating policy:', JSON.stringify(newPolicy, null, 2));
      
      const result = await client
        .api('/policies/claimsMappingPolicies')
        .post(newPolicy);
      
      setCreateDialogOpen(false);
      setNewPolicyName("");
      setNewPolicyDescription(""); // Still clear this for UI cleanup
      setNewPolicyJson("");
      setSuccessMessage(`Policy "${newPolicyName}" created successfully`);
      setSnackbarOpen(true);
      await fetchPolicies();
    } catch (error: any) {
      console.error("Error creating policy:", error);
      setErrorMessage(`Failed to create policy: ${error.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle updating a policy
  const handleUpdatePolicy = async () => {
    if (!selectedPolicy) return;
    
    // Validate JSON before attempting to update policy
    const validationResult = validateClaimsMappingPolicyJson(selectedPolicyJson);
    
    if (!validationResult.isValid) {
      setJsonError(validationResult.error || "Invalid JSON format");
      return;
    }
    
    try {
      setLoading(true);
      // JIT elevate to write scope
      const client = await getGraphClientWithScopes(["Policy.ReadWrite.ApplicationConfiguration"]);
      
      const updatedPolicy = {
        displayName: selectedPolicy.displayName,
        definition: [JSON.stringify(validationResult.parsedJson)]
      };
      
      await client
        .api(`/policies/claimsMappingPolicies/${selectedPolicy.id}`)
        .patch(updatedPolicy);
      
      setIsEditMode(false);
      setSuccessMessage(`Policy "${selectedPolicy.displayName}" updated successfully`);
      setSnackbarOpen(true);
      await fetchPolicies();
      
      // Refresh the selected policy with updated data
      const policies = await client
        .api('/policies/claimsMappingPolicies')
        .get();
      
      const updatedPolicyData = policies.value.find((p: ClaimsMappingPolicy) => p.id === selectedPolicy.id);
      if (updatedPolicyData) {
        handleSelectPolicy(updatedPolicyData);
      }
    } catch (error: any) {
      console.error("Error updating policy:", error);
      setErrorMessage(`Failed to update policy: ${error.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  // Add this function to your component
  const handleValidateJson = () => {
    const validationResult = validateClaimsMappingPolicyJson(
      isEditMode ? selectedPolicyJson : newPolicyJson
    );
    
    if (validationResult.isValid) {
      setSuccessMessage("Policy JSON is valid");
      setJsonError("");
      setSnackbarOpen(true);
    } else {
      setJsonError(validationResult.error || "Invalid JSON format");
    }
  };

  // Handle deleting a policy
  const handleDeletePolicy = async (policyId: string) => {
    if (!window.confirm("Are you sure you want to delete this policy? This action cannot be undone.")) {
      return;
    }
    
    try {
      setLoading(true);
      // JIT elevate to write scope
      const client = await getGraphClientWithScopes(["Policy.ReadWrite.ApplicationConfiguration"]);
      
      await client
        .api(`/policies/claimsMappingPolicies/${policyId}`)
        .delete();
      
      setSelectedPolicy(null);
      setSelectedPolicyData(null);
      setSelectedPolicyJson("");
      setSuccessMessage("Policy deleted successfully");
      setSnackbarOpen(true);
      await fetchPolicies();
    } catch (error: any) {
      console.error("Error deleting policy:", error);
      setErrorMessage(`Failed to delete policy: ${error.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle assigning a policy to a service principal
  const handleAssignPolicy = async () => {
    if (!selectedPolicy || !selectedAppId) {
      setErrorMessage("Please select both a policy and an application");
      return;
    }
    
    try {
      setLoading(true);
      
      // Find the service principal for the selected application
      const servicePrincipal = servicePrincipals.find(sp => sp.id === selectedAppId);
      if (!servicePrincipal) {
        throw new Error("Selected service principal not found");
      }
      
  // JIT elevate to write scope for assignment
  const client = await getGraphClientWithScopes(["Policy.ReadWrite.ApplicationConfiguration"]);
      
      // The correct way to assign a policy to a service principal
      await client
        .api(`/servicePrincipals/${servicePrincipal.id}/claimsMappingPolicies/$ref`)
        .post({
          "@odata.id": `https://graph.microsoft.com/v1.0/policies/claimsMappingPolicies/${selectedPolicy.id}`
        });
      
      setAssignDialogOpen(false);
      setSelectedAppId("");
      setSuccessMessage(`Policy "${selectedPolicy.displayName}" assigned to "${servicePrincipal.displayName}" successfully`);
      setSnackbarOpen(true);
      
      // Refresh assignments
      const assignments = await client
        .api(`/policies/claimsMappingPolicies/${selectedPolicy.id}/appliesTo`)
        .get();
      
      setPolicyAssignments(prev => ({
        ...prev,
        [selectedPolicy.id]: assignments.value.map((sp: any) => sp.id)
      }));
    } catch (error: any) {
      console.error("Error assigning policy:", error);
      setErrorMessage(`Failed to assign policy: ${error.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle removing a policy assignment
  const handleRemovePolicyAssignment = async (policyId: string, servicePrincipalId: string) => {
    if (!window.confirm("Are you sure you want to remove this policy assignment?")) {
      return;
    }
    
    try {
      setLoading(true);
      // JIT elevate to write scope for removal
      const client = await getGraphClientWithScopes(["Policy.ReadWrite.ApplicationConfiguration"]);
      
      // The correct way to remove a policy from a service principal
      await client
        .api(`/servicePrincipals/${servicePrincipalId}/claimsMappingPolicies/${policyId}/$ref`)
        .delete();
      
      setSuccessMessage("Policy assignment removed successfully");
      setSnackbarOpen(true);
      
      // Update local state to reflect the change
      setPolicyAssignments(prev => ({
        ...prev,
        [policyId]: prev[policyId].filter(id => id !== servicePrincipalId)
      }));
    } catch (error: any) {
      console.error("Error removing policy assignment:", error);
      setErrorMessage(`Failed to remove policy assignment: ${error.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle copying content to clipboard
  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  // Handle opening the template dialog
  const handleOpenTemplateDialog = () => {
    setTemplateDialogOpen(true);
  };

  // Handle applying a template
  const handleApplyTemplate = () => {
    if (!selectedTemplate) return;
    
    const template = claimTemplates[selectedTemplate];
    
    try {
      const currentPolicy = selectedPolicyJson ? JSON.parse(selectedPolicyJson) : {
        ClaimsMappingPolicy: {
          Version: 1,
          IncludeBasicClaimSet: "true",
          ClaimsSchema: []
        }
      };
      
      // Add template claims to the existing claims schema
      if (!currentPolicy.ClaimsMappingPolicy) {
        currentPolicy.ClaimsMappingPolicy = {
          Version: 1,
          IncludeBasicClaimSet: "true",
          ClaimsSchema: []
        };
      }
      
      if (!currentPolicy.ClaimsMappingPolicy.ClaimsSchema) {
        currentPolicy.ClaimsMappingPolicy.ClaimsSchema = [];
      }
      
      // Add the template claims
      template.forEach(claim => {
        // Check if this claim already exists
        const existingClaimIndex = currentPolicy.ClaimsMappingPolicy.ClaimsSchema.findIndex(
          (c: any) => c.ID === claim.id && c.Source === claim.source
        );
        
        if (existingClaimIndex >= 0) {
          // Update existing claim
          currentPolicy.ClaimsMappingPolicy.ClaimsSchema[existingClaimIndex] = {
            Source: claim.source,
            ID: claim.id,
            ...(claim.saml ? { SamlClaimType: claim.jwtClaimType } : { JwtClaimType: claim.jwtClaimType }),
            ...(claim.defaultValue ? { DefaultValue: claim.defaultValue } : {})
          };
        } else {
          // Add new claim
          currentPolicy.ClaimsMappingPolicy.ClaimsSchema.push({
            Source: claim.source,
            ID: claim.id,
            ...(claim.saml ? { SamlClaimType: claim.jwtClaimType } : { JwtClaimType: claim.jwtClaimType }),
            ...(claim.defaultValue ? { DefaultValue: claim.defaultValue } : {})
          });
        }
      });
      
      setSelectedPolicyJson(JSON.stringify(currentPolicy, null, 2));
      setTemplateDialogOpen(false);
      setSelectedTemplate("");
      
      // If we're in edit mode, this doesn't auto-save - user still needs to click Save
      setSuccessMessage("Template applied to policy definition. Don't forget to save your changes.");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error applying template:", error);
      setErrorMessage("Failed to apply template. Check your policy format.");
    }
  };

  // If token is missing, render a friendly error and back action (render-time guard only)
  if (!accessToken) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Token unavailable
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The access token for Microsoft Graph is missing or expired. Please go back and reopen the Claims Mapping Policy tool.
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={onBack}>Back</Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header Card */}
      <Card
        variant="outlined"
        sx={{
          mb: 4,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#f4f8fb',
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
          }}
        >
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{
              mr: 2,
              borderRadius: 2,
              pl: 1.5,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
                borderColor: "primary.main",
                color: "primary.main"
              }
            }}
          >
            Back
          </Button>
          
          <Box
            sx={{
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2,
              py: 0.5,
            }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                letterSpacing: '-0.01em',
              }}
            >
              Claims Mapping Policy Tool
            </Typography>
            
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              }}
            >
              Create, edit, and assign custom claims to your applications
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Main content */}
      <Grid container spacing={3}>
        {/* Left column - Policies list */}
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ 
                p: 2, 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                backgroundColor: '#f9f9f9'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Claims Mapping Policies
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreateDialog}
                  sx={{ borderRadius: 2 }}
                >
                  New Policy
                </Button>
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : policies.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="textSecondary">
                    No claims mapping policies found. Click 'New Policy' to create one.
                  </Typography>
                </Box>
              ) : (
                <List sx={{ height: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                  {policies.map((policy) => (
                    <ListItem
                      key={policy.id}
                      disablePadding
                      sx={{
                        borderLeft: selectedPolicy?.id === policy.id ? 3 : 0,
                        borderColor: 'primary.main'
                      }}
                    >
                      <ListItemButton
                        selected={selectedPolicy?.id === policy.id}
                        onClick={() => handleSelectPolicy(policy)}
                        sx={{
                          backgroundColor: selectedPolicy?.id === policy.id ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.04)'
                          }
                        }}
                      >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography 
                              variant="subtitle1" 
                              component="span" // Change from default p to span
                              sx={{ fontWeight: 600 }}
                            >
                              {policy.displayName}
                            </Typography>
                            {policy.isOrganizationDefault && (
                              <Chip
                                size="small"
                                label="Default"
                                color="primary"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography 
                              variant="body2" 
                              component="span" // Change from default p to span
                              sx={{ color: 'text.secondary', display: 'block' }}
                            >
                              {policy.description || "No description"}
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                              <Chip 
                                size="small" 
                                label={`${policyAssignments[policy.id]?.length || 0} Assignments`} 
                                variant="outlined"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            </Box>
                          </Box>
                        }
                      />
                      </ListItemButton>
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePolicy(policy.id);
                          }}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right column - Policy details */}
        <Grid item xs={12} md={8}>
          {!selectedPolicy ? (
                <Card variant="outlined" sx={{ 
                  borderRadius: 2, 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  // Update these styles to position content higher in the card
                  justifyContent: 'flex-start',  // Changed from 'center' to 'flex-start'
                  alignItems: 'center', 
                  p: 4,
                  pt: 8  // Add top padding to push content down a bit from the top edge
                }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                Select a policy to view or edit its details
              </Typography>
              
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateDialog}
                sx={{ mt: 2, borderRadius: 2 }}
              >
                Create a New Policy
              </Button>
              
              <Box sx={{ mt: 6, width: '100%' }}>

                <Accordion sx={{ mt: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <CodeIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Example Policy Structures
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Basic Claims Policy
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        mt: 1,
                        mb: 3,
                        backgroundColor: 'rgba(0, 0, 0, 0.03)',
                        position: 'relative',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        overflow: 'auto'
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleCopyToClipboard(policyExamples.basic)}
                        sx={{ position: 'absolute', top: 8, right: 8 }}
                      >
                        {copySuccess ? <CheckIcon color="success" /> : <ContentCopyIcon />}
                      </IconButton>
                      <pre style={{ margin: 0 }}>{policyExamples.basic}</pre>
                    </Paper>

                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 3 }}>
                      Advanced Claims Policy with Optional Claims
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        mt: 1,
                        backgroundColor: 'rgba(0, 0, 0, 0.03)',
                        position: 'relative',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        overflow: 'auto'
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleCopyToClipboard(policyExamples.advanced)}
                        sx={{ position: 'absolute', top: 8, right: 8 }}
                      >
                        {copySuccess ? <CheckIcon color="success" /> : <ContentCopyIcon />}
                      </IconButton>
                      <pre style={{ margin: 0 }}>{policyExamples.advanced}</pre>
                    </Paper>
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      About Claims Mapping
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body1" paragraph>
                      Claims mapping policies allow you to customize the claims that are emitted in tokens for specific applications. You can include additional claims from user attributes, directory extensions, or transform existing claims.
                    </Typography>
                    
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
                      When to use claims mapping:
                    </Typography>
                    <ul>
                      <li>Include custom attributes in tokens</li>
                      <li>Rename or transform standard attributes</li>
                      <li>Create application-specific rules for token issuance</li>
                    </ul>
                    
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
                      Types of Claims:
                    </Typography>
                    {claimsExplanations.map((explanation, index) => (
                      <Box key={index} sx={{ mb: 2, mt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {explanation.title}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {explanation.description}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 0.5 }}>
                          {explanation.examples.map((example, i) => (
                            <Chip 
                              key={i} 
                              label={example} 
                              size="small" 
                              variant="outlined" 
                              sx={{ mr: 0.5, mb: 0.5 }} 
                            />
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
                
              </Box>
            </Card>
          ) : (
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ 
                  p: 2, 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                  backgroundColor: '#f9f9f9'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {selectedPolicy.displayName}
                    {selectedPolicy.isOrganizationDefault && (
                      <Chip
                        size="small"
                        label="Default"
                        color="primary"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Typography>
                  <Box>
                    {isEditMode ? (
                      <>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setIsEditMode(false);
                            handleSelectPolicy(selectedPolicy);
                          }}
                          sx={{ mr: 1, borderRadius: 2 }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<SaveIcon />}
                          onClick={handleUpdatePolicy}
                          sx={{ borderRadius: 2 }}
                        >
                          Save
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outlined"
                          startIcon={<AssignmentIcon />}
                          onClick={() => setAssignDialogOpen(true)}
                          sx={{ mr: 1, borderRadius: 2 }}
                        >
                          Assign
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<EditIcon />}
                          onClick={() => setIsEditMode(true)}
                          sx={{ borderRadius: 2 }}
                        >
                          Edit
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
                
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs value={tabIndex} onChange={handleTabChange} indicatorColor="primary">
                    <Tab label="Policy Definition" />
                    <Tab label="Assignments" />
                  </Tabs>
                </Box>
                
                <TabPanel value={tabIndex} index={0}>
                  {isEditMode ? (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Edit Policy Definition
                        </Typography>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={handleOpenTemplateDialog}
                          startIcon={<AddIcon />}
                          sx={{ borderRadius: 2 }}
                        >
                          Add Claim Template
                        </Button>
                      </Box>
                      
                      {jsonError && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJsonError("")}>
                          {jsonError}
                        </Alert>
                      )}
                      
                      <TextField
                        label="Policy Definition (JSON)"
                        multiline
                        rows={20}
                        fullWidth
                        value={selectedPolicyJson}
                        onChange={(e) => setSelectedPolicyJson(e.target.value)}
                        variant="outlined"
                        error={!!jsonError}
                        InputProps={{
                          sx: { 
                            fontFamily: 'monospace', 
                            fontSize: '0.875rem',
                            borderRadius: 2
                          }
                        }}
                      />
                    </Box>
                  ) : (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Policy Definition
                        </Typography>
                        <IconButton 
                          size="small"
                          onClick={() => handleCopyToClipboard(selectedPolicyJson)}
                        >
                          {copySuccess ? <CheckIcon color="success" /> : <ContentCopyIcon />}
                        </IconButton>
                      </Box>
                      
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          backgroundColor: 'rgba(0, 0, 0, 0.03)',
                          borderRadius: 2,
                          maxHeight: 500,
                          overflow: 'auto'
                        }}
                      >
                        <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {selectedPolicyJson}
                        </pre>
                      </Paper>
                      
                      {selectedPolicyData?.ClaimsMappingPolicy?.ClaimsSchema?.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                            Claims Summary
                          </Typography>
                          
                          <Grid container spacing={2}>
                            {selectedPolicyData.ClaimsMappingPolicy.ClaimsSchema.map((claim: any, index: number) => {
                              const isJwtClaim = !!claim.JwtClaimType;
                              const claimType = isJwtClaim ? claim.JwtClaimType : claim.SamlClaimType || 'Unnamed Claim';
                              const claimSource = claim.Source || 'Unknown';
                              const claimId = claim.ID || 'Unknown ID';
                              
                              return (
                                <Grid item xs={12} key={index}>
                                  <Paper
                                    variant="outlined"
                                    sx={{
                                      p: 0,
                                      borderRadius: 2,
                                      borderLeft: '4px solid',
                                      borderLeftColor: isJwtClaim ? 'primary.main' : 'secondary.main',
                                      overflow: 'hidden',
                                      transition: 'box-shadow 0.2s',
                                      '&:hover': {
                                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                      }
                                    }}
                                  >
                                    <Box sx={{ 
                                      display: 'flex', 
                                      flexDirection: {xs: 'column', sm: 'row'},
                                      alignItems: 'stretch'
                                    }}>
                                      {/* Claim type and badge */}
                                      <Box sx={{ 
                                        p: 2, 
                                        width: {xs: '100%', sm: '30%'},
                                        bgcolor: theme => isJwtClaim ? 
                                          alpha(theme.palette.primary.main, 0.05) : 
                                          alpha(theme.palette.secondary.main, 0.05),
                                        borderRight: {xs: 'none', sm: '1px solid'},
                                        borderBottom: {xs: '1px solid', sm: 'none'},
                                        borderColor: 'divider',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between'
                                      }}>
                                        <Box>
                                          <Typography variant="subtitle2" sx={{ 
                                            fontWeight: 600,
                                            wordBreak: 'break-word',
                                            lineHeight: 1.4
                                          }}>
                                            {claimType}
                                          </Typography>
                                          <Chip
                                            size="small"
                                            label={isJwtClaim ? 'JWT' : 'SAML'}
                                            color={isJwtClaim ? 'primary' : 'secondary'}
                                            variant="outlined"
                                            sx={{ mt: 1 }}
                                          />
                                        </Box>
                                      </Box>
                                      
                                      {/* Claim details */}
                                      <Box sx={{ 
                                        p: 2, 
                                        flexGrow: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center'
                                      }}>
                                        <Box sx={{ 
                                          display: 'grid', 
                                          gridTemplateColumns: {xs: '80px 1fr', md: '100px 1fr'},
                                          rowGap: 1.5
                                        }}>
                                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                            Source:
                                          </Typography>
                                          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                            {claimSource}
                                          </Typography>
                                          
                                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                            ID:
                                          </Typography>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ 
                                              wordBreak: 'break-word',
                                              fontFamily: 'monospace',
                                              fontSize: '0.8rem',
                                              bgcolor: 'rgba(0,0,0,0.04)',
                                              p: 0.5,
                                              borderRadius: 1,
                                              maxWidth: '100%'
                                            }}>
                                              {claimId}
                                            </Typography>
                                            <Tooltip title="Copy to clipboard">
                                              <IconButton
                                                size="small"
                                                onClick={() => handleCopyToClipboard(claimId)}
                                                sx={{ flexShrink: 0 }}
                                              >
                                                <ContentCopyIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          </Box>
                                          
                                          {claim.DefaultValue && (
                                            <>
                                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                Default:
                                              </Typography>
                                              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                                {claim.DefaultValue}
                                              </Typography>
                                            </>
                                          )}
                                        </Box>
                                      </Box>
                                    </Box>
                                  </Paper>
                                </Grid>
                              );
                            })}
                          </Grid>
                        </Box>
                      )}
                    </Box>
                  )}
                </TabPanel>
                
                <TabPanel value={tabIndex} index={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Policy Assignments
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<AssignmentIcon />}
                      onClick={() => setAssignDialogOpen(true)}
                      sx={{ borderRadius: 2 }}
                    >
                      Assign to Application
                    </Button>
                  </Box>
                  
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : !policyAssignments[selectedPolicy.id] || policyAssignments[selectedPolicy.id].length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center', backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: 2 }}>
                      <Typography color="textSecondary">
                        This policy is not assigned to any applications yet.
                      </Typography>
                    </Box>
                  ) : (
                    <List>
                      {policyAssignments[selectedPolicy.id].map(spId => {
                        const sp = servicePrincipals.find(sp => sp.id === spId);
                        return (
                          <ListItem
                            key={spId}
                            sx={{
                              borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                              py: 1
                            }}
                          >
                            <ListItemText
                              primary={
                                <Typography component="span" variant="body1"> {/* Change to span */}
                                  {sp?.displayName || "Unknown Application"}
                                </Typography>
                              }
                              secondary={
                                <Box component="span"> {/* Change Box to use span instead of div */}
                                  <Typography 
                                    variant="caption" 
                                    component="span" 
                                    sx={{ display: 'block' }}
                                  >
                                    App ID: {sp?.appId || "Unknown"}
                                  </Typography>
                                  {sp?.appOwnerOrganizationId && (
                                    <Box component="span" sx={{ display: 'block', mt: 0.5 }}> {/* Use span instead of div */}
                                      <Chip
                                        size="small"
                                        label={sp.appOwnerOrganizationId === "f8cdef31-a31e-4b4a-93e4-5f571e91255a" ? "First Party" : "Third Party"}
                                        color={sp.appOwnerOrganizationId === "f8cdef31-a31e-4b4a-93e4-5f571e91255a" ? "primary" : "secondary"}
                                        variant="outlined"
                                      />
                                    </Box>
                                  )}
                                </Box>
                              }
                              primaryTypographyProps={{ component: 'span' }}  // Ensure primary text is a span
                              secondaryTypographyProps={{ component: 'span' }} // Ensure secondary text is a span
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                onClick={() => handleRemovePolicyAssignment(selectedPolicy.id, spId)}
                                size="small"
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </TabPanel>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Create Policy Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          resetNewPolicyForm();
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Create New Claims Mapping Policy</DialogTitle>
        <DialogContent>
        <Box sx={{ my: 2 }}>
          <TextField
            label="Policy Name"
            fullWidth
            value={newPolicyName}
            onChange={(e) => setNewPolicyName(e.target.value)}
            required
            sx={{ mb: 2 }}
          />
          
          <Box sx={{ mb: 3 }}>
            <Alert severity="warning" sx={{ mb: 1 }}>
              Currently, description field is not supported due to Microsoft Graph API limitations
            </Alert>
            <TextField
              label="Description (Placeholder for future use)"
              fullWidth
              value={newPolicyDescription}
              onChange={(e) => setNewPolicyDescription(e.target.value)}
              disabled={true}
              helperText="This field is disabled as it's not supported by the API"
            />
          </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Policy Definition (JSON)
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const basePolicy = {
                    ClaimsMappingPolicy: {
                      Version: 1,
                      IncludeBasicClaimSet: "true",
                      ClaimsSchema: []
                    }
                  };
                  setNewPolicyJson(JSON.stringify(basePolicy, null, 2));
                }}
                sx={{ borderRadius: 2 }}
              >
                Use Template
              </Button>
            </Box>
            
            {jsonError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setJsonError("")}>
                {jsonError}
              </Alert>
            )}
            
            <TextField
              multiline
              rows={12}
              fullWidth
              value={newPolicyJson}
              onChange={(e) => setNewPolicyJson(e.target.value)}
              variant="outlined"
              error={!!jsonError}
              placeholder='{"ClaimsMappingPolicy": {"Version": 1, "IncludeBasicClaimSet": "true", "ClaimsSchema": []}}'
              InputProps={{
                sx: { 
                  fontFamily: 'monospace', 
                  fontSize: '0.875rem',
                  borderRadius: 2
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleValidateJson}
            startIcon={<DoneIcon />}
            sx={{ 
            borderRadius: 2,
            boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)',
            }}
          >
            Validate JSON
          </Button>
          </Box>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            resetNewPolicyForm();
          }} color="inherit">
            Cancel
          </Button>
          <Button 
          onClick={handleCreatePolicy} 
          color="primary" 
          variant="contained"
          disabled={loading}
          sx={{ borderRadius: 2 }}
          >
          {loading ? <CircularProgress size={24} /> : "Create Policy"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Policy Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Assign Policy to Application</DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">
                Select an application to assign policy "{selectedPolicy?.displayName}"
              </Typography>
              
              <Button
                variant="outlined"
                size="small"
                onClick={toggleAppTypesFilter}
                startIcon={showAllAppTypes ? <FilterListOffIcon /> : <FilterListIcon />}
                sx={{ borderRadius: 2 }}
              >
                {showAllAppTypes ? "Show Web Apps Only" : "Show All App Types"}
              </Button>
            </Box>

            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by application name or App ID"
              value={appSearchQuery}
              onChange={(e) => setAppSearchQuery(e.target.value)}
              sx={{
                mt: 2,
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: appSearchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear search"
                      onClick={() => setAppSearchQuery("")}
                      edge="end"
                      size="small"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="app-select-label">Application</InputLabel>
              <Select
                labelId="app-select-label"
                value={selectedAppId}
                onChange={(e) => setSelectedAppId(e.target.value as string)}
                label="Application"
                displayEmpty
                renderValue={(selected) => {
                    if (!selected) {
                    return <Typography color="text.secondary"></Typography>;
                    }
                  const selectedSp = servicePrincipals.find(sp => sp.id === selected);
                  return (
                    <Box>
                      <Typography variant="body1">{selectedSp?.displayName || 'Unknown App'}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        App ID: {selectedSp?.appId || 'N/A'}
                      </Typography>
                    </Box>
                  );
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                {filteredServicePrincipals
                  .filter(sp => !policyAssignments[selectedPolicy?.id || ""]?.includes(sp.id))
                  .map((sp) => (
                    <MenuItem key={sp.id} value={sp.id} sx={{ display: 'block', py: 1 }}>
                      <Typography variant="body1">{sp.displayName || 'Unknown App'}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        App ID: {sp.appId || 'N/A'}
                      </Typography>
                    </MenuItem>
                  ))
                }
              </Select>
              <FormHelperText>
                {showAllAppTypes 
                  ? `Showing all ${filteredServicePrincipals.length} applications matching your search`
                  : `Showing web, SPA, and API applications only`}
              </FormHelperText>
            </FormControl>

            {/* Add counter chips to show filtered results */}
            {filteredServicePrincipals.length > 0 && (
              <Box sx={{ display: 'flex', mt: 2, gap: 1 }}>
                <Chip 
                  size="small" 
                  label={`${filteredServicePrincipals.length} apps available`} 
                  color="primary" 
                  variant="outlined"
                />
                {filteredServicePrincipals.length !== servicePrincipals.length && (
                  <Chip 
                    size="small" 
                    label={`${servicePrincipals.length - filteredServicePrincipals.length} filtered out`} 
                    color="default" 
                    variant="outlined"
                  />
                )}
              </Box>
            )}

            {/* Add feedback for empty search results */}
            {servicePrincipals.length > 0 && filteredServicePrincipals.length === 0 && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                No applications match your search criteria{appSearchQuery ? ` "${appSearchQuery}"` : ""}.
                {!showAllAppTypes && (
                  <Box sx={{ mt: 1 }}>
                    <Button 
                      size="small" 
                      onClick={toggleAppTypesFilter} 
                      startIcon={<FilterListOffIcon />}
                      variant="outlined"
                      sx={{ borderRadius: 2 }}
                    >
                      Show All App Types
                    </Button>
                  </Box>
                )}
              </Alert>
            )}
            
            {servicePrincipals.length === 0 && !loading && (
              <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                No service principals found or failed to load.
              </Alert>
            )}
            
            <Alert severity="info" sx={{ mt: 3 }}>
              <AlertTitle>About Claim Mapping Assignment</AlertTitle>
              Assigning a claims mapping policy to an application will customize the claims in tokens issued to that application.
              Any existing claims mapping policy assignment will be replaced.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleAssignPolicy} 
            color="primary" 
            variant="contained"
            disabled={loading || !selectedAppId}
            sx={{ borderRadius: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : "Assign Policy"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Dialog */}
      <Dialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Claim Template</DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Select a claim template to add to your policy
            </Typography>
            
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="template-select-label">Claim Template</InputLabel>
              <Select
                labelId="template-select-label"
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value as string)}
                label="Claim Template"
              >
                {Object.keys(claimTemplates).map((templateName) => (
                  <MenuItem key={templateName} value={templateName}>
                    {templateName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {selectedTemplate && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(0, 0, 0, 0.03)', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Template Preview:
                </Typography>
                <pre style={{ margin: '10px 0 0', overflow: 'auto', fontSize: '0.75rem' }}>
                  {JSON.stringify(claimTemplates[selectedTemplate], null, 2)}
                </pre>
              </Box>
            )}
            
            <Alert severity="info" sx={{ mt: 3 }}>
              <AlertTitle>About Claim Templates</AlertTitle>
              Adding a template will insert or update claims in your policy definition. For directory extension attributes, you'll need to replace &lt;guid&gt; with your actual extension ID.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleApplyTemplate} 
            color="primary" 
            variant="contained"
            disabled={!selectedTemplate}
            sx={{ borderRadius: 2 }}
          >
            Apply Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      {errorMessage && (
        <Snackbar 
          open={!!errorMessage} 
          autoHideDuration={6000} 
          onClose={() => setErrorMessage("")}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setErrorMessage("")} severity="error" sx={{ width: '100%' }}>
            {errorMessage}
          </Alert>
        </Snackbar>
      )}
      
      {/* Success Snackbar */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={4000} 
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ClaimsMappingPolicyTool;
